import React, {useCallback, useEffect, useState} from 'react'
import * as yup from 'yup'
import {useForm} from 'react-hook-form'
import {toast} from 'react-toastify'
import {yupResolver} from '@hookform/resolvers/yup'
import {
  Icon,
  ParagraphText,
  Input,
  TextArea,
  Checkbox,
  Button,
  withModal,
} from '@thryvlabs/maverick'
import {
  ModalContainer,
  InputContainer,
  TextAreaContainer,
  PurchaseIntentItem,
  PurchaseIntentInfoItem,
  PurchaseIntentContainer,
  PurchaseIntentListContainer,
  PurchaseIntentGroupContainer,
  OnboardingContainer,
} from '../components/ui/onboarding-program'
import {NewOrderLayout} from '../components/new-order-layout'
import {CartPaymentDetails} from '../components/cart-payment-details'
import {AccountInfoBanner} from '../components/account-info-banner'
import {useLocation} from 'react-router'
import {PhoneNumberUtil} from 'google-libphonenumber'
import {CcSidebar} from '../components/cc-sidebar'
import BookAppointments from '../components/appointments/book-appointments'
import {
  THRYV_STARTER_CODES,
  GROWTH_PACKAGES,
  MC_2_0_SIGNATUREPLANS,
} from '../utils/constants'
import {isTgpFoundationFamilyCode} from '../utils/tgp-catalog-flags'

const phoneUtil = PhoneNumberUtil.getInstance()

const PURCHASE_INTENTS = [
  {
    title: 'Build Your Brand',
    description:
      'Initial onboarding will focus on your online presence - syncing listings, claiming GMB, connect website widgets, establishing an online reputation, creating social media profile/posting, building a professional website and more.',
    icon: 'https://lirp-cdn.multiscreensite.com/40644fd4111f4fc1929b5a8493d5708f/dms3rep/multi/opt/swatchbook_grey_orange_rgb-80w.png',
    options: [
      'Consistent Impression',
      'Be the Clear Choice',
      'Interact Easily',
    ],
  },
  {
    title: 'Stay Organized',
    description:
      'Initial onboarding will focus on centralizing your business with one Inbox and CRM – setting up calendar, appointments and scheduling, creating users and staff accounts, automated messages, creating estimates/invoices and more.',
    icon: 'https://lirp-cdn.multiscreensite.com/40644fd4111f4fc1929b5a8493d5708f/dms3rep/multi/opt/layer_group_grey_orange_rgb-80w.png',
    options: [
      'Simplify Your Business Operations',
      'Centralize Customer Info',
      'Automate Manual Tasks',
    ],
  },
  {
    title: 'Get Paid/Get Credit',
    description:
      'Initial onboarding will focus on getting paid faster - online payments, estimates/invoices, marketing automation, customer communication with text and email, review generation, social media posting and more.',
    icon: 'https://lirp-cdn.multiscreensite.com/40644fd4111f4fc1929b5a8493d5708f/dms3rep/multi/opt/dollar_sack_grey_orange_rgb-480w.png',
    options: [
      'Contact-less Transactions & Payments',
      'Get Credit',
      'Increase Revenue & Cash-Flow',
    ],
  },
  {
    title: 'Communicate',
    description:
      'Initial onboarding will focus on communicating more efficiently – Inbox, CRM and Client Portal, email sync, customer communication with text and email, marketing automation, connecting website widgets, follow-ups and reminders and more.',
    icon: 'https://lirp-cdn.multiscreensite.com/40644fd4111f4fc1929b5a8493d5708f/dms3rep/multi/opt/handshake_grey_orange_rgb-480w.png',
    options: [
      'Communicate with Customers on Their Terms',
      'Build & Maintain Relationships',
      'Create Supporters',
    ],
  },
]

const CODES_WITH_KICKOFF_CALL = [
  'bc_plus-5-thryv-m2m',
  'bc_pro-5-thryv-m2m',
  'bc_unlm-5-thryv-m2m',
  'bc-6-thryv-m2m',
  'bc_ent-6-thryv-m2m',
  'bc_ent-unlm-5-thryv-m2m',
  'mc_plus-5-thryv-m2m',
  'mc_pro-5-thryv-m2m',
  'mc_prov2-5-thryv-m2m', // Signature (Monthly) — v2 MC Pro with website bundled
  'signature-5-thryv-annual', // Signature (Annual) — annual counterpart of mc_prov2
  'signature_plus-5-thryv-m2m',
  'signature_plus-5-thryv-annual',
  'seobase-5-thryv-m2m',
  'wc_plus-5-thryv-m2m',
  'maxhvac-5-thryv-m2m',
  'maxroof-5-thryv-m2m',
  'maxelect-5-thryv-m2m',
  'maxplumb-5-thryv-m2m',
  'keap_max-5-thryv-m2m',
  'keap_ultimate-5-thryv-m2m',
  'keap_max-5-thryv-part-m2m',
  'keap_ultimate-5-thryv-part-m2m',
  'addon-5-leadslift-m2m',
  'addon-6-mc-socialcontentgen-m2m',
  'tl-mc-5-thryv-m2m',
  'premier-5-thryv-annual',
  'mc_premier-5-thryv-m2m',
  // FULSAS-11317: website/logo/video products now require kickoff call instead of creative call
  'addon-5-website_difm',
  // FULSAS-12281/BUSREQ-15061: AI Visibility Boost add-on reuses the existing
  // MC onboarding call (no new appointment type) — required on new orders.
  'addon-5-visibilityboost',
]

const CODES_WITH_THRYV_SUCCESS_SERVICES_CALL = ['addon-5-stratcontent-m2m']

// FULSAS-11317: one-time items that now require a kickoff call instead of creative call.
// These live in selectedCentersAndApps.oneTimes and are not evaluated by CODES_WITH_KICKOFF_CALL.
const ONETIMES_WITH_KICKOFF_CALL = [
  'thryv5page', // 5 Extra Website Pages
  'thryvlogo4', // Professional Designed Logo
  'addon-5-custvid', // Custom Video Shoot
]

// FULSAS-11317: entitlement codes (within center.entitlements) that require kickoff call
// when newly added to an existing center in an upgrade. These are NOT top-level apps or
// centers, so they are not covered by CODES_WITH_KICKOFF_CALL.
const ENTITLEMENTS_WITH_KICKOFF_CALL = [
  'ent-5-website', // Professionally Designed Website (bundled within MC Signature)
  'ent-5-custvideo', // Custom Video (bundled entitlement)
  'ent-5-socialcontentgen', // Social Content Generation (bundled entitlement)
]

const ONBOARDING_REQUIRED_4PLAN_UPGRADES = [
  'addon-4-mc-m2m',
  'addon-4-mc-semi',
  'addon-4-mcpro-m2m',
]

const FREE_ACCOUNT_CODES = [
  'appmarket-5-thryv-m2m',
  'cc_basicpls-5-thryv-m2m',
  'cc_basic-5-thryv-m2m',
  'addon-5-signatures_usage',
  'app-websitebuilder',
]

const REQUIRED_FIELD_MESSAGE = 'This field is required'
const MIN_CHARACTERS_REQUIRED = 'Must be at least 3 characters'
const MAX_CHARACTERS_REQUIRED = 'Must be at most 120 characters'

function comparePlans({
  selectedCentersAndApps,
  defaultCentersAndApps,
  isFreeAccount,
}) {
  // true -- no kickoff call
  // false -- kickoff call required
  const isMCTrialOnboardingRequiredInCart =
    selectedCentersAndApps?.centers?.find(({code: itemCode}) =>
      MC_2_0_SIGNATUREPLANS.includes(itemCode),
    )

  if (
    isMCTrialOnboardingRequiredInCart &&
    isMCTrialOnboardingRequiredInCart?.onboardingRequired?.value === 'Yes'
  ) {
    return false
  }

  const isMCFoundationPresent = defaultCentersAndApps?.centers?.find(({code}) =>
    isTgpFoundationFamilyCode(code),
  )
  const isMCUpgradeInCart = selectedCentersAndApps?.centers?.some(
    ({code: itemCode}) =>
      itemCode === 'mc_plus-5-thryv-m2m' || itemCode === 'mc_pro-5-thryv-m2m',
  )

  if (isMCFoundationPresent && isMCUpgradeInCart) {
    return false
  }

  const isSEOSelected =
    selectedCentersAndApps?.centers?.find(({name}) => name === 'AEO & SEO') ||
    selectedCentersAndApps?.apps?.find(({name}) => name === 'AEO & SEO')
  const isSEOAlreadyPresent =
    defaultCentersAndApps?.centers?.find(({name}) => name === 'AEO & SEO') ||
    defaultCentersAndApps?.apps?.find(({name}) => name === 'AEO & SEO')

  if (isSEOSelected && !isSEOAlreadyPresent) {
    return false
  }

  const isThryvLeadsSelected =
    selectedCentersAndApps?.centers?.find(({name}) => name === 'Thryv Leads') ||
    selectedCentersAndApps?.apps?.find(({name}) => name === 'Thryv Leads')
  const isThryvLeadsAlreadyPresent =
    defaultCentersAndApps?.centers?.find(({name}) => name === 'Thryv Leads') ||
    defaultCentersAndApps?.apps?.find(({name}) => name === 'Thryv Leads')

  if (isThryvLeadsSelected && !isThryvLeadsAlreadyPresent) {
    return false
  }

  const isHomeServicesSelected = selectedCentersAndApps?.apps?.find(({code}) =>
    [
      'maxhvac-5-thryv-m2m',
      'maxroof-5-thryv-m2m',
      'maxelect-5-thryv-m2m',
      'maxplumb-5-thryv-m2m',
    ].includes(code),
  )
  const isHomeServicesAlreadyPresent = defaultCentersAndApps?.apps?.find(
    ({code}) =>
      [
        'maxhvac-5-thryv-m2m',
        'maxroof-5-thryv-m2m',
        'maxelect-5-thryv-m2m',
        'maxplumb-5-thryv-m2m',
      ].includes(code),
  )

  if (isHomeServicesSelected && !isHomeServicesAlreadyPresent) {
    return false
  }

  const isMCTrialPlanSelected = selectedCentersAndApps?.centers?.find(
    ({code}) =>
      ['mc_premier-5-thryv-m2m', 'premier-5-thryv-annual'].includes(code),
  )
  const isMCTrialPlanAlreadyPresent = defaultCentersAndApps?.centers?.find(
    ({code}) =>
      ['mc_premier-5-thryv-m2m', 'premier-5-thryv-annual'].includes(code),
  )

  if (isMCTrialPlanSelected && !isMCTrialPlanAlreadyPresent) {
    return false
  }

  const isThryvSocialContent = selectedCentersAndApps?.apps?.find(
    ({code}) => code === 'addon-6-mc-socialcontentgen-m2m',
  )
  const isThryvSocialContentAlreadyPresent = defaultCentersAndApps?.apps?.find(
    ({code}) => code === 'addon-6-mc-socialcontentgen-m2m',
  )

  if (isThryvSocialContent && !isThryvSocialContentAlreadyPresent) {
    return false
  }

  const isLLSelected = selectedCentersAndApps?.apps?.find(
    ({name}) => name === 'Leads Lift',
  )
  const isLLAlreadyPresent = defaultCentersAndApps?.apps?.find(
    ({name}) => name === 'Leads Lift',
  )

  if (isLLSelected && !isLLAlreadyPresent) {
    return false
  }

  const isWorkforceCenterSelected = selectedCentersAndApps?.centers?.find(
    ({code}) => code === 'wc_plus-5-thryv-m2m',
  )
  const isWorkforceCenterAlreadyPresent = defaultCentersAndApps?.centers?.find(
    ({code}) => code === 'wc_plus-5-thryv-m2m',
  )

  if (isWorkforceCenterSelected && !isWorkforceCenterAlreadyPresent) {
    return false
  }

  // Check if BC or MC is present
  const isBcSelected = selectedCentersAndApps?.centers?.find(
    ({name}) => name.split(' ')[0] === 'Business',
  )
  const isMcSelected = selectedCentersAndApps?.centers?.find(
    ({name}) => name.split(' ')[0] === 'Marketing',
  )
  const isBcAlreadyPresent = defaultCentersAndApps?.centers?.find(
    ({name}) => name.split(' ')[0] === 'Business',
  )
  const isMcAlreadyPresent = defaultCentersAndApps?.centers?.find(
    ({name}) => name.split(' ')[0] === 'Marketing',
  )
  if (
    (isBcSelected && !isBcAlreadyPresent && isMcAlreadyPresent) ||
    (isMcSelected && !isMcAlreadyPresent && isBcAlreadyPresent)
  ) {
    return false
  }

  const isNewSocialAds = selectedCentersAndApps?.apps?.some(
    ({name}) =>
      name.includes('Social Ads') &&
      !defaultCentersAndApps?.apps?.some(({name}) =>
        name.includes('Social Ads'),
      ),
  )
  if (isNewSocialAds) return false

  const newApps = selectedCentersAndApps?.apps?.filter(
    ({code: selectedCode}) =>
      !defaultCentersAndApps?.apps?.some(({code}) => selectedCode === code),
  )

  const newThryvSuccessServices = newApps?.some(({code}) =>
    CODES_WITH_THRYV_SUCCESS_SERVICES_CALL.includes(code),
  )

  if (newThryvSuccessServices) return false

  const isStarterPlanSelected = selectedCentersAndApps?.centers?.find(
    ({code}) => THRYV_STARTER_CODES.includes(code),
  )
  if (isStarterPlanSelected) {
    // FULSAS-12295: opting into onboarding (toggle lives on the Growth
    // Package add-on) requires a kickoff call, overriding the Starter
    // plan's usual "no kickoff call" default.
    const growthPackageAddon = selectedCentersAndApps?.apps?.find(({code}) =>
      GROWTH_PACKAGES.includes(code),
    )
    if (growthPackageAddon?.includeOnboarding?.value === 'Yes') return false
    return true
  }

  // MC Foundation is the $0 tier — it never requires a kickoff call.
  // Its groupTitle ('Marketing Center') won't match the 'Thryv' groupTitle of
  // MC 2.0 plans already in the account, so the catch-all below would
  // incorrectly flag it. Exempt it explicitly before that check runs.
  const isMCFoundationSelected = selectedCentersAndApps?.centers?.find(
    ({code}) => isTgpFoundationFamilyCode(code),
  )
  const isMCFoundationAlreadyPresent = defaultCentersAndApps?.centers?.find(
    ({code}) => isTgpFoundationFamilyCode(code),
  )
  if (isMCFoundationSelected && !isMCFoundationAlreadyPresent) return true

  const defaultCenterNames = defaultCentersAndApps?.centers?.map(
    ({groupTitle}) => groupTitle,
  )
  const defaultAppNames = defaultCentersAndApps?.apps?.map(
    ({groupTitle}) => groupTitle,
  )
  const allDefaultNames = [
    ...(defaultCenterNames || []),
    ...(defaultAppNames || []),
  ]
  // FULSAS-11317: standalone website app and one-time items now require kickoff call.
  const isWebsiteDifmSelected = selectedCentersAndApps?.apps?.find(
    ({code}) => code === 'addon-5-website_difm',
  )
  const isWebsiteDifmAlreadyPresent = defaultCentersAndApps?.apps?.find(
    ({code}) => code === 'addon-5-website_difm',
  )
  if (isWebsiteDifmSelected && !isWebsiteDifmAlreadyPresent) return false

  // FULSAS-12281/BUSREQ-15061: AI Visibility Boost add-on newly added to an
  // existing account must trigger the same onboarding/kickoff call as other
  // add-ons here — the groupTitle catch-all below does not reliably cover
  // new "Thryv Add-Ons"-grouped products, so this needs its own explicit check.
  const isVisibilityBoostSelected = selectedCentersAndApps?.apps?.find(
    ({code}) => code === 'addon-5-visibilityboost',
  )
  const isVisibilityBoostAlreadyPresent = defaultCentersAndApps?.apps?.find(
    ({code}) => code === 'addon-5-visibilityboost',
  )
  if (isVisibilityBoostSelected && !isVisibilityBoostAlreadyPresent)
    return false

  // FULSAS-11317: kickoff entitlement added inside an existing center (e.g. ent-5-website in MC Signature)
  const isNewKickoffEntitlement = selectedCentersAndApps?.centers?.some(
    center => {
      const newEnt = center.entitlements?.find(({code}) =>
        ENTITLEMENTS_WITH_KICKOFF_CALL.includes(code),
      )
      if (!newEnt) return false
      const defaultCenter = defaultCentersAndApps?.centers?.find(
        ({code}) => code === center.code,
      )
      return !defaultCenter?.entitlements?.some(
        ({code}) => code === newEnt.code,
      )
    },
  )
  if (isNewKickoffEntitlement) return false

  const isNewKickoffOneTime = selectedCentersAndApps?.oneTimes?.some(
    ({code, alreadyIncluded}) =>
      ONETIMES_WITH_KICKOFF_CALL.includes(code) && !alreadyIncluded,
  )
  if (isNewKickoffOneTime) return false

  if (
    isMCTrialOnboardingRequiredInCart &&
    isMCTrialOnboardingRequiredInCart?.onboardingRequired?.value === 'No'
  ) {
    return true
  }

  // WARNING: The catch-all below uses groupTitle.split(' ')[0] to match products.
  // Products sharing the same group_name (e.g., 23 products in "Thryv Add-Ons")
  // will falsely match each other. All products requiring kickoff calls MUST have
  // explicit checks above this block. Do NOT rely on the catch-all for new products.
  const centersMatch = selectedCentersAndApps?.centers?.every(
    ({groupTitle}) => {
      const centerType = groupTitle?.split(' ')[0]
      if (
        centerType === 'Reporting' ||
        centerType === 'Workforce' ||
        centerType === 'Partner'
      )
        return true

      return allDefaultNames?.some(
        defaultName =>
          defaultName?.includes(centerType) &&
          !(groupTitle === 'Thryv Leads' && isFreeAccount),
      )
    },
  )
  if (!centersMatch) return false

  const appsWithGroupTitle = selectedCentersAndApps?.apps?.filter(
    ({groupTitle}) => groupTitle === 'Thryv Leads',
  )
  const appsMatch =
    !appsWithGroupTitle?.length ||
    appsWithGroupTitle.every(({groupTitle}) =>
      allDefaultNames?.some(
        defaultName =>
          defaultName?.includes(groupTitle?.split(' ')[0]) &&
          !(groupTitle === 'Thryv Leads' && isFreeAccount),
      ),
    )
  return appsMatch
}

const shouldDisplayKickOffCall = ({centers, apps, oneTimes}) => {
  // true -- no kickoff call
  // false -- kickoff call required
  if (
    centers?.some(center => center.name === 'Thryv Leads') ||
    apps?.some(app => app.name === 'Thryv Leads')
  ) {
    return false
  }

  // FULSAS-12295: Thryv Starter — kickoff call required only if the
  // onboarding opt-in toggle (on the Growth Package add-on) is Yes
  const starterCenter = centers?.find(({code}) =>
    THRYV_STARTER_CODES.includes(code),
  )
  const growthPackageAddon = apps?.find(({code}) =>
    GROWTH_PACKAGES.includes(code),
  )
  if (starterCenter && growthPackageAddon?.includeOnboarding?.value === 'Yes') {
    return false
  }

  // mc_prov2 / signature-annual: kickoff depends on onboardingRequired + website entitlement
  const conditionalMCCenter = centers?.find(({code}) =>
    MC_2_0_SIGNATUREPLANS.includes(code),
  )
  if (conditionalMCCenter) {
    const onboardingRequired =
      conditionalMCCenter?.onboardingRequired?.value === 'Yes'
    const hasWebsiteEnt = (conditionalMCCenter?.entitlements ?? []).some(
      ({code}) => code === 'ent-5-website',
    )
    if (onboardingRequired || hasWebsiteEnt) return false
    // onboardingRequired === 'No' and no website entitlement — fall through
    // to check other items in the cart before returning true
  }

  const kickoffCenter = centers?.find(
    center =>
      CODES_WITH_KICKOFF_CALL.includes(center.code) &&
      !MC_2_0_SIGNATUREPLANS.includes(center.code),
  )
  if (kickoffCenter) {
    return false
  }
  const isKickOffCallNeededForApp = apps?.find(({code}) =>
    CODES_WITH_KICKOFF_CALL.includes(code),
  )
  if (isKickOffCallNeededForApp) {
    return false
  }
  // FULSAS-11317: one-time items (logo, 5pages, custvid) are not in centers/apps —
  // check oneTimes separately.
  const isKickOffCallNeededForOneTime = oneTimes?.find(({code}) =>
    ONETIMES_WITH_KICKOFF_CALL.includes(code),
  )
  if (isKickOffCallNeededForOneTime) {
    return false
  }
  return true
}

const setIsKickoffNotRequiredCC = ({
  isUpgrade,
  defaultCentersAndApps,
  selectedCentersAndApps,
  isFreeAccount,
}) => {
  const result = isUpgrade
    ? comparePlans({
        selectedCentersAndApps: {
          ...selectedCentersAndApps,
          centers: selectedCentersAndApps?.centers?.filter(
            ({name}) => !name.includes('SEO') || name === 'AEO & SEO',
          ),
          apps: selectedCentersAndApps?.apps?.filter(
            ({name}) => !name.includes('SEO') || name === 'AEO & SEO',
          ),
        },
        defaultCentersAndApps,
        isFreeAccount,
      })
    : shouldDisplayKickOffCall({
        centers: selectedCentersAndApps?.centers,
        apps: selectedCentersAndApps?.apps,
        oneTimes: selectedCentersAndApps?.oneTimes,
      })
  return result
}

const SCHEMA = yup.object().shape({
  firstName: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  lastName: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  email: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .email('Must be a valid email'),
  mobile: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .test(
      'validate-phone-format',
      'Incorrect phone number format',
      function (value) {
        try {
          if (!value) {
            return false
          }
          const country = this.options.context.componentProps.selectedCountry
          value = value.replace(/[^\d]/g, '')
          const parsedPhone = phoneUtil.parse(value, country)
          return phoneUtil.isValidNumber(parsedPhone)
        } catch {
          return false
        }
      },
    ),
  firstPurchaseIntent: yup.object().shape({
    option: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
    icon: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
    title: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
  }),
  secondPurchaseIntent: yup.object().shape({
    option: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
    icon: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
    title: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
  }),
  thirdPurchaseIntent: yup.object().shape({
    option: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
    icon: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
    title: yup.string().when(['isUpgrade', 'CCOnboarding'], {
      is: false,
      then: yup.string().required(REQUIRED_FIELD_MESSAGE),
      otherwise: yup.string().notRequired(),
    }),
  }),
  csmExtraInfo: yup.string(),
  CCOnboarding: yup.boolean(),
  isUpgrade: yup.boolean(),
  isKickoffNotRequired: yup.boolean(),
  onboardingCallDate: yup.string().when(['isUpgrade', 'isKickoffNotRequired'], {
    is: false,
    then: yup.string(),
    otherwise: yup.string().notRequired(),
  }),
  showCreativeCall: yup.boolean(),
  creativeCallDate: yup.string().when('showCreativeCall', {
    is: true,
    then: yup.string(),
  }),
  showThryvSuccessServicesCall: yup.boolean(),
  thryvSuccessServicesDate: yup.string().when('showThryvSuccessServicesCall', {
    is: true,
    then: yup.string(),
  }),
})

function PurchaseIntentOptionsList({
  close,
  prevStep,
  purchaseIntentInfo,
  setPurchaseItem,
  selectedPurchaseIntent,
  selectedPurchaseIntents,
}) {
  const [selectedOption, setSelectedOption] = useState(
    selectedPurchaseIntent || {},
  )
  const handleSelection = () => {
    setPurchaseItem(selectedOption)
    close()
  }

  return (
    <>
      <ParagraphText variant="lg" className="font-weight-bold">
        Step 2 - Pick a Subset
      </ParagraphText>
      <div className="d-flex justify-content-center align-items-center mb-2">
        <img
          src={purchaseIntentInfo.icon}
          alt="Purchase Intent Item Icon"
          width="20"
          className="mr-2"
        />
        <h4 className="m-0 font-weight-bold">{purchaseIntentInfo.title}</h4>
      </div>
      <ul className="p-0">
        {purchaseIntentInfo.options.map(option => {
          if (selectedPurchaseIntents.includes(option)) {
            return (
              <PurchaseIntentInfoItem key={option} className="mb-2 disabled">
                {option}
              </PurchaseIntentInfoItem>
            )
          }
          return (
            <PurchaseIntentInfoItem
              key={option}
              className={`mb-2 ${
                selectedOption.option === option ? 'selected' : ''
              }`}
              onClick={() =>
                setSelectedOption({
                  option,
                  icon: purchaseIntentInfo.icon,
                  title: purchaseIntentInfo.title,
                })
              }
            >
              {option}
            </PurchaseIntentInfoItem>
          )
        })}
      </ul>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Button variant="primary" onClick={prevStep}>
          Back
        </Button>
        <Button
          variant="primary"
          disabled={!selectedOption.option}
          onClick={handleSelection}
        >
          Save & Close
        </Button>
      </div>
    </>
  )
}

function PurchaseIntentList({close, nextStep}) {
  return (
    <PurchaseIntentListContainer>
      {PURCHASE_INTENTS.map(({title, description, icon, options}) => (
        <PurchaseIntentItem key={title}>
          <div className="d-flex justify-content-center align-items-center">
            <img
              src={icon}
              alt="Purchase Intent Item Icon"
              width="20"
              className="mr-2"
            ></img>
            <h4 className="m-0 font-weight-bold">{title}</h4>
          </div>
          <p>{description}</p>
          <div
            className="d-flex justify-content-center align-items-center purchase_intent_button"
            onClick={() => nextStep({title, icon, options})}
          >
            <ParagraphText
              variant="reg"
              color="thryv-orange-500"
              className="m-0 mr-2 font-weight-bold"
            >
              {title}
            </ParagraphText>
            <Icon
              type="solid"
              variant="arrowRight"
              color="#FF5000"
              height="15"
              width="15"
              className="m-0"
            />
          </div>
        </PurchaseIntentItem>
      ))}
    </PurchaseIntentListContainer>
  )
}
function Modal({
  close,
  setSelectedPurchaseIntent,
  selectedPurchaseIntents,
  selectedPurchaseIntent,
}) {
  const TOTAL_STEPS = 2
  const [purchaseIntentInfo, setPurchaseIntentInfo] = useState({})
  const [currentStep, setCurrentStep] = useState(1)

  const nextStep = data => {
    setPurchaseIntentInfo(data)
    let _currentStep = currentStep
    if (currentStep >= TOTAL_STEPS - 1) {
      _currentStep = TOTAL_STEPS
    } else {
      _currentStep = currentStep + 1
    }
    setCurrentStep(_currentStep)
  }
  const prevStep = () => {
    let _currentStep = currentStep
    if (currentStep <= 1) {
      _currentStep = 1
    } else {
      _currentStep = currentStep - 1
    }
    setCurrentStep(_currentStep)
  }
  const setPurchaseItem = purchaseItem => {
    if (selectedPurchaseIntents.includes(purchaseItem)) {
      setSelectedPurchaseIntent(null)
      return
    }
    setSelectedPurchaseIntent(purchaseItem)
  }
  return (
    <ModalContainer>
      {currentStep === 1 && (
        <PurchaseIntentList nextStep={nextStep} close={close} />
      )}
      {currentStep === 2 && (
        <PurchaseIntentOptionsList
          nextStep={nextStep}
          prevStep={prevStep}
          purchaseIntentInfo={purchaseIntentInfo}
          setPurchaseItem={setPurchaseItem}
          close={close}
          selectedPurchaseIntents={selectedPurchaseIntents}
          selectedPurchaseIntent={selectedPurchaseIntent}
        />
      )}
    </ModalContainer>
  )
}

function PurchaseIntent({showModal, purchaseIntent, title, errors, name}) {
  if (purchaseIntent?.option) {
    return (
      <PurchaseIntentContainer onClick={showModal}>
        <div className="d-flex justify-content-center align-items-center mb-2">
          <img
            src={purchaseIntent.icon}
            alt="Purchase Intent Item Icon"
            width="20"
            className="mr-2"
          />
          <h4 className="m-0 font-weight-bold">{purchaseIntent.title}</h4>
        </div>
        <ParagraphText
          variant="reg"
          color="thryv-gray-medium-500"
          className="m-0"
        >
          {purchaseIntent.option}
        </ParagraphText>
      </PurchaseIntentContainer>
    )
  }

  return (
    <div>
      <PurchaseIntentContainer onClick={showModal}>
        <Icon
          type="solid"
          variant="circlePlus"
          color="#FF5000"
          className="mb-3"
        />
        <ParagraphText
          variant="reg"
          color="thryv-gray-medium-500"
          className="m-0"
        >
          {title}
        </ParagraphText>
      </PurchaseIntentContainer>
      {errors.errors[name]?.option.message && (
        <ParagraphText
          variant="reg"
          color="utility-error-danger"
          className="m-0 mt-2 text-center"
        >
          {errors.errors[name].option.message}
        </ParagraphText>
      )}
    </div>
  )
}

const PurchaseIntentModal = withModal(PurchaseIntent, Modal)

const OnboardingProgram = ({
  prevStep,
  nextStep,
  industryCategoryType,
  selectedAddons,
  accountInformation,
  creativeApptId,
  thryvSuccessServicesApptId,
  setCreativeApptId,
  setThryvSuccessServicesApptId,
  selectedCountry,
  setKickoffApptId,
  kickoffApptId,
  onboardingCallDate,
  setOnboardingCallDate,
  creativeCallDate,
  setCreativeCallDate,
  thryvSuccessServicesCallDate,
  setThryvSuccessServicesCallDate,
  setAllSelectedPurchaseIntent,
  allSelectedPurchaseIntent,
  selectedPlan,
  currentPlan,
  currentPlanName,
  currentStep,
  setCartProducts,
  cartProducts,
  selectedIndustryId,
  isWebsiteIncluded,
  isSocialBoostIncluded,
  selectedCentersAndApps,
  defaultCentersAndApps,
  upgradeAccountInfo,
  previewRequestFinished,
  eligibleTransitionCenters,
  isCohort,
  order5NUId,
  isCancelOrder5,
  cancelOrder5,
  captureOrderNewUpgrade,
  order5OnboardingApptId,
  order5CreativeApptId,
  order5ThryvSuccessServicesApptId,
  setOrder5CreativeApptId,
  setOrder5OnboardingApptId,
  setOrder5ThryvSuccessServicesApptId,
  isWebsiteValid,
  isNewTLMCAccessible,
  keapAccount = false,
  isBillingNoticeDisplayed,
  setIsBillingNoticeDisplayed,
  all5PlanPrices = [],
}) => {
  console.log(
    'isBillingNoticeDisplayed from onboarding-program.js is :: ',
    isBillingNoticeDisplayed,
  )
  const [useAccountOwnerInfo, setUseAccountOwnerInfo] = useState(true)
  const [firstPurchaseIntent, setFirstPurchaseIntent] = useState({})
  const [secondPurchaseIntent, setSecondPurchaseIntent] = useState({})
  const [thirdPurchaseIntent, setThirdPurchaseIntent] = useState({})
  const hasCalledNextStep = React.useRef(false)
  const [displayCreativeCallModal, setDisplayCreativeCallModal] =
    useState(false)
  const [displayKickOffCall, setDisplayKickOffCall] = useState(true)
  const [displayThryvSuccessServicesCall, setDisplayThryvSuccessServicesCall] =
    useState(false)

  const [onboardingConfirmed, setOnboardingConfirmed] = useState(false)
  const [creativeConfirmed, setCreativeConfirmed] = useState(false)
  const [thryvSuccessServicesConfirmed, setThryvSuccessServicesConfirmed] =
    useState(false)
  const [continueDisabled, setContinueDisabled] = useState(true)
  const [isNewThryvLeads, setNewThryvLead] = useState(false) // new field inclusion for tl mc
  const {pathname} = useLocation()
  const [isApptSkipped, setIsApptSkipped] = useState({
    kickoff: false,
    creative: false,
    'thryv-success-services': false,
  })
  const isUpgrade = pathname === '/order/upgrade'
  const isFreeAccount = !(
    defaultCentersAndApps?.centers?.some(
      center => !FREE_ACCOUNT_CODES.includes(center.code),
    ) ||
    defaultCentersAndApps?.apps?.some(
      app => !FREE_ACCOUNT_CODES.includes(app.code),
    )
  )
  const isUpgradingFromLegacyKeap =
    isUpgrade &&
    defaultCentersAndApps?.centers?.some(
      center =>
        center.code.startsWith('keap_') && center.groupTitle === 'Hidden',
    )
  const isKickoffNotRequiredCC =
    isUpgradingFromLegacyKeap ||
    setIsKickoffNotRequiredCC({
      isUpgrade,
      defaultCentersAndApps,
      selectedCentersAndApps,
      isFreeAccount,
    })
  // FULSAS-12295: Thryv Starter onboarding opt-in (toggle lives on the
  // Growth Package add-on) drives the region picker in the kickoff call flow.
  const isThryvStarterInOrder = selectedCentersAndApps?.centers?.some(
    ({code}) => THRYV_STARTER_CODES.includes(code),
  )
  const growthPackageOnboardingAddon = selectedCentersAndApps?.apps?.find(
    ({code}) => GROWTH_PACKAGES.includes(code),
  )
  const isThryvStarterOnboarding =
    isThryvStarterInOrder &&
    growthPackageOnboardingAddon?.includeOnboarding?.value === 'Yes'
  // Detect if TSS call is needed (SCC addon added in upgrade).
  // Computed during render so it's available before any useEffect runs.
  const needsTSSCall =
    isUpgrade &&
    !isKickoffNotRequiredCC &&
    selectedCentersAndApps?.apps?.some(
      ({code, alreadyIncluded}) =>
        CODES_WITH_THRYV_SUCCESS_SERVICES_CALL.includes(code) &&
        !alreadyIncluded,
    )
  // Re-run comparePlans without SCC to check if SCC was the SOLE trigger.
  // If comparePlans returns true without SCC, no other product needs kickoff.
  const isSCCOnlyTrigger =
    needsTSSCall &&
    comparePlans({
      selectedCentersAndApps: {
        ...selectedCentersAndApps,
        centers: selectedCentersAndApps?.centers?.filter(
          ({name}) => !name.includes('SEO') || name === 'AEO & SEO',
        ),
        apps: selectedCentersAndApps?.apps
          ?.filter(({name}) => !name.includes('SEO') || name === 'AEO & SEO')
          ?.filter(
            ({code}) => !CODES_WITH_THRYV_SUCCESS_SERVICES_CALL.includes(code),
          ),
      },
      defaultCentersAndApps,
      isFreeAccount,
    })
  const {mobile, email, lastName, firstName} = accountInformation

  const {
    register,
    handleSubmit,
    formState: {errors},
    setValue,
    watch,
    trigger,
    clearErrors,
  } = useForm({
    defaultValues: {
      firstName,
      lastName,
      email,
      mobile,
      isUpgrade: false,
      CCOnboarding: false,
      isKickoffNotRequired: isKickoffNotRequiredCC || false,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(SCHEMA),
    context: {
      componentProps: {
        onboardingConfirmed,
        creativeConfirmed,
        thryvSuccessServicesConfirmed,
        selectedCountry: selectedCountry.isoValue,
      },
    },
  })
  const isNewOrder =
    pathname === '/order/new' &&
    selectedIndustryId !== 13 &&
    selectedIndustryId !== 12
  const isCurrentPlanSync = currentPlan?.includes('sync')
  const isSelectedPlanSuperior = [
    'plus',
    'professional',
    'unlimited',
    'unlimitedenterprise',
  ].includes(selectedPlan?.name)
  const isCurrentPlanDemo =
    currentPlan?.includes('demo') || currentPlan?.includes('freetrial')
  const isSyncUpgrade = isCurrentPlanSync && isSelectedPlanSuperior
  const isPurchaseIntentVisible =
    isNewOrder ||
    isSyncUpgrade ||
    isCurrentPlanDemo ||
    (isUpgrade && selectedCentersAndApps)
  const is4MCSelected = selectedAddons?.some(({planCode}) =>
    ONBOARDING_REQUIRED_4PLAN_UPGRADES.includes(planCode),
  )

  const isMCAlreadyIncluded =
    !selectedCentersAndApps &&
    upgradeAccountInfo?.addOns.some(({addOnCode}) =>
      ONBOARDING_REQUIRED_4PLAN_UPGRADES.includes(addOnCode),
    )

  const newAddons = selectedAddons?.filter(
    ({type, product, planCode}) =>
      type !== 'selectedPlan' &&
      upgradeAccountInfo?.addOns?.every(({name}) => !name.includes(product)),
  )

  const isSocialAdsInNewAddons =
    newAddons?.some(({product}) => product.includes('Social Ads')) || false
  const isTLMCInNewAddons =
    newAddons?.some(({product}) => product.includes('Thryv Leads')) || false
  const isLLInNewAddons =
    newAddons?.some(({product}) => product.includes('Leads Lift')) || false
  const isWorkforceCenterInNewAddons =
    newAddons?.some(
      ({product, planCode}) =>
        product?.includes('Workforce Center') ||
        planCode?.includes('_wc') ||
        planCode?.startsWith('wc_'),
    ) || false
  const isSEOInNewAddons =
    newAddons?.some(
      ({product, tempLineActionCode}) =>
        product === 'AEO & SEO' && tempLineActionCode === 'New',
    ) || false

  const isThryvSocialPostsInNewAddons =
    newAddons?.some(({planCode}) =>
      planCode.includes('addon-4-mc-socialcontentgen-m2m'),
    ) || false

  const isStrategicContentCreationInNewAddons =
    newAddons?.some(({planCode}) =>
      planCode.includes('addon-4-stratcontent-m2m'),
    ) || false

  const isOnboardingRequired4Upgrade =
    isUpgrade &&
    ((is4MCSelected && !isMCAlreadyIncluded) ||
      isSocialAdsInNewAddons ||
      isTLMCInNewAddons ||
      isLLInNewAddons ||
      isWorkforceCenterInNewAddons ||
      isSEOInNewAddons ||
      isThryvSocialPostsInNewAddons ||
      isStrategicContentCreationInNewAddons)

  const [csmExtraInfo, firstNameInput, lastNameInput, emailInput, mobileInput] =
    watch(['csmExtraInfo', 'firstName', 'lastName', 'email', 'mobile'])

  const handleOnChange = () => setUseAccountOwnerInfo(!useAccountOwnerInfo)

  const saveCallData = data => {
    if (data.callType === 'kickoff') {
      setValue('onboardingCallDate', data.longDate)
      setOnboardingCallDate(data)
    } else if (data.callType === 'creative') {
      setValue('creativeCallDate', data.longDate)
      setCreativeCallDate(data)
    } else if (data.callType === 'thryv-success-services') {
      setValue('thryvSuccessServicesCallDate', data.longDate)
      setThryvSuccessServicesCallDate(data)
    }
  }

  const handleNextStep = useCallback(() => {
    const data = {
      onBoardingProgramData: {
        intents: [
          firstPurchaseIntent,
          secondPurchaseIntent,
          thirdPurchaseIntent,
        ],
        onboardingCallDate,
        creativeCallDate,
        thryvSuccessServicesCallDate,
        csmExtraInfo,
        accountOwnerInfoUsed: useAccountOwnerInfo,
        accountInfo: {
          firstName: firstNameInput,
          lastName: lastNameInput,
          email: emailInput,
          mobile: mobileInput,
        },
      },
    }
    if (onboardingCallDate?.longDate && !kickoffApptId) {
      toast.warning('Please confirm Thryv Kickoff Call date.')
      return
    }
    nextStep(data)
  }, [
    creativeCallDate,
    thryvSuccessServicesCallDate,
    csmExtraInfo,
    emailInput,
    firstNameInput,
    firstPurchaseIntent,
    lastNameInput,
    mobileInput,
    nextStep,
    onboardingCallDate,
    secondPurchaseIntent,
    thirdPurchaseIntent,
    useAccountOwnerInfo,
    kickoffApptId,
  ])

  const getDisabledButton = () => {
    // // in case onboarding confirmed we are setting continue as true for tl mc changes
    // console.log({
    //   onboardingCallDate,
    //   onboardingConfirmed,
    //   isApptSkipped,
    //   isKickoffNotRequiredCC,
    //   displayCreativeCallModal,
    // })

    const isOnboardingCompletedOrSkipped = displayKickOffCall
      ? onboardingConfirmed || isApptSkipped.kickoff
      : true
    const isCreativeRequiredAndConfirmedOrSkipped = displayCreativeCallModal
      ? creativeConfirmed || isApptSkipped.creative
      : true
    const isThryvSuccessServicesRequiredAndConfirmedOrSkipped =
      displayThryvSuccessServicesCall
        ? thryvSuccessServicesConfirmed ||
          isApptSkipped['thryv-success-services']
        : true

    if (
      isOnboardingCompletedOrSkipped &&
      isCreativeRequiredAndConfirmedOrSkipped &&
      isThryvSuccessServicesRequiredAndConfirmedOrSkipped
    ) {
      return false
    }

    if (
      (isKickoffNotRequiredCC || isApptSkipped.kickoff) &&
      !displayCreativeCallModal
    )
      return false
    // ========================================
    // logic added to make kickoff call mandatory
    let is4VSEOAddonsAdded = false
    if (selectedAddons) {
      let is4VNewSEOExist = selectedAddons.find(item => {
        return [
          'addon-4-seobase-m2m',
          'addon-4-seokey-m2m',
          'addon-4-seopb-m2m',
        ].includes(item?.planCode)
      })
      if (is4VNewSEOExist) {
        let isSEOAlreadyIncluded = upgradeAccountInfo.addOns.find(
          item => item.addOnCode === 'addon-4-seobase-m2m',
        )
        if (!isSEOAlreadyIncluded) {
          is4VSEOAddonsAdded = true
        }
      }
    }
    // tl mc changes
    if (isNewThryvLeads && isNewTLMCAccessible) {
      return true
    }
    //
    if (is4VSEOAddonsAdded) {
      return true
    }
    if (
      displayKickOffCall &&
      typeof continueDisabled === 'boolean' &&
      continueDisabled
    ) {
      return true
    }

    const is5Order = !!selectedCentersAndApps
    if (!is5Order) {
      const isSelectedPlan = selectedAddons?.find(
        plan => plan.type === 'selectedPlan',
      )

      if (isSelectedPlan?.planCode !== upgradeAccountInfo.planCode) {
        return true
      }
    }

    // ==============================================
    return !isUpgrade ||
      (!isKickoffNotRequiredCC && selectedCentersAndApps) ||
      displayCreativeCallModal ||
      displayThryvSuccessServicesCall ||
      isOnboardingRequired4Upgrade
      ? !continueDisabled
      : false
  }
  useEffect(() => {
    setAllSelectedPurchaseIntent({
      firstPurchaseIntent:
        Object.values(firstPurchaseIntent).length > 0
          ? firstPurchaseIntent
          : allSelectedPurchaseIntent?.firstPurchaseIntent,
      secondPurchaseIntent:
        Object.values(secondPurchaseIntent).length > 0
          ? secondPurchaseIntent
          : allSelectedPurchaseIntent?.secondPurchaseIntent,
      thirdPurchaseIntent:
        Object.values(thirdPurchaseIntent).length > 0
          ? thirdPurchaseIntent
          : allSelectedPurchaseIntent?.thirdPurchaseIntent,
    })

    setFirstPurchaseIntent(
      Object.values(firstPurchaseIntent).length > 0
        ? firstPurchaseIntent
        : allSelectedPurchaseIntent?.firstPurchaseIntent,
    )
    setSecondPurchaseIntent(
      Object.values(secondPurchaseIntent).length > 0
        ? secondPurchaseIntent
        : allSelectedPurchaseIntent?.secondPurchaseIntent,
    )
    setThirdPurchaseIntent(
      Object.values(thirdPurchaseIntent).length > 0
        ? thirdPurchaseIntent
        : allSelectedPurchaseIntent?.thirdPurchaseIntent,
    )
  }, [
    allSelectedPurchaseIntent?.firstPurchaseIntent,
    allSelectedPurchaseIntent?.secondPurchaseIntent,
    allSelectedPurchaseIntent?.thirdPurchaseIntent,
    firstPurchaseIntent,
    secondPurchaseIntent,
    thirdPurchaseIntent,
    setAllSelectedPurchaseIntent,
  ])

  useEffect(() => {
    // tl mc logic
    if (selectedAddons && isNewTLMCAccessible) {
      let isThryvLeadsExist = selectedAddons.find(
        item =>
          item?.addOnCode?.includes('tl-') ||
          (item?.planCode?.includes('tl-') &&
            item.tempLineActionCode === 'New'),
      )
      if (isThryvLeadsExist) {
        setNewThryvLead(true)
        setContinueDisabled(true)
      }
    }
    // tl mc logic
  }, [])

  useEffect(() => {
    // For v4 orders (selectedCentersAndApps is null), let book-appointments.js
    // handle kickoff display — comparePlans can't evaluate v4 data correctly.
    if (!selectedCentersAndApps) return
    if (isKickoffNotRequiredCC && !isSyncUpgrade) {
      setDisplayKickOffCall(false)
    } else if (!isKickoffNotRequiredCC) {
      if (needsTSSCall) {
        // Always show TSS call when SCC is newly added
        setDisplayThryvSuccessServicesCall(true)
        if (isSCCOnlyTrigger) {
          // SCC is the sole reason comparePlans returned false — hide kickoff
          setDisplayKickOffCall(false)
        } else {
          // Another product (SEO, BC, TL, etc.) also needs kickoff — show both
          setDisplayKickOffCall(true)
        }
      } else {
        setDisplayKickOffCall(true)
      }
    }
  }, [
    selectedCentersAndApps,
    isSyncUpgrade,
    isUpgrade,
    isKickoffNotRequiredCC,
    needsTSSCall,
    isSCCOnlyTrigger,
  ])

  // FULSAS-12295: if the Thryv Starter onboarding opt-in is switched back to
  // No (e.g. after navigating back to Catalog) and no other product in the
  // cart requires a kickoff call, clear any appointment state scheduled under
  // the opt-in so it doesn't linger orphaned.
  const prevIsThryvStarterOnboardingRef = React.useRef(isThryvStarterOnboarding)
  useEffect(() => {
    if (
      prevIsThryvStarterOnboardingRef.current &&
      !isThryvStarterOnboarding &&
      isKickoffNotRequiredCC &&
      kickoffApptId
    ) {
      setKickoffApptId(null)
      setOnboardingCallDate(null)
      setIsApptSkipped(prev => ({...prev, kickoff: false}))
    }
    prevIsThryvStarterOnboardingRef.current = isThryvStarterOnboarding
  }, [isThryvStarterOnboarding, isKickoffNotRequiredCC, kickoffApptId])

  useEffect(() => {
    setValue('firstPurchaseIntent', firstPurchaseIntent)
  }, [firstPurchaseIntent, setValue])

  useEffect(() => {
    setValue('secondPurchaseIntent', secondPurchaseIntent)
  }, [secondPurchaseIntent, setValue])

  useEffect(() => {
    setValue('thirdPurchaseIntent', thirdPurchaseIntent)
  }, [thirdPurchaseIntent, setValue])

  useEffect(() => {
    if (!displayKickOffCall && displayCreativeCallModal) {
      setContinueDisabled(creativeApptId || isApptSkipped.creative)
    } else if (displayCreativeCallModal && isUpgrade && !onboardingCallDate) {
      setContinueDisabled(creativeApptId || isApptSkipped.creative)
    } else if (displayCreativeCallModal) {
      setContinueDisabled(
        (creativeApptId || isApptSkipped.creative) &&
          (kickoffApptId || isApptSkipped.kickoff),
      )
    } else if (displayThryvSuccessServicesCall && displayKickOffCall) {
      setContinueDisabled(
        (thryvSuccessServicesApptId ||
          isApptSkipped['thryv-success-services']) &&
          (kickoffApptId || isApptSkipped.kickoff),
      )
    } else if (displayThryvSuccessServicesCall) {
      setContinueDisabled(
        thryvSuccessServicesApptId || isApptSkipped['thryv-success-services'],
      )
    } else {
      setContinueDisabled(kickoffApptId || isApptSkipped.kickoff)
    }
  }, [
    displayCreativeCallModal,
    kickoffApptId,
    creativeApptId,
    thryvSuccessServicesApptId,
    selectedIndustryId,
    displayKickOffCall,
    creativeCallDate,
    isUpgrade,
    onboardingCallDate,
    isApptSkipped.creative,
    isApptSkipped.kickoff,
    isApptSkipped['thryv-success-services'],
    selectedAddons, // extra param added for tl mc logic
    isNewThryvLeads, // extra param added for tl mc logic
    displayThryvSuccessServicesCall,
  ])

  useEffect(() => {
    if (useAccountOwnerInfo) {
      setValue('firstName', firstName)
      setValue('lastName', lastName)
      setValue('email', email)
      setValue('mobile', mobile)
    } else {
      setValue('firstName', '')
      setValue('lastName', '')
      setValue('email', '')
      setValue('mobile', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAccountOwnerInfo])

  useEffect(() => {
    if (isNewOrder || isSyncUpgrade || isOnboardingRequired4Upgrade) {
      if (isKickoffNotRequiredCC) {
        setValue('isUpgrade', true)
      } else {
        setValue('isUpgrade', false)
      }
    } else {
      setValue('isUpgrade', true)
    }
  }, [
    pathname,
    setValue,
    isNewOrder,
    isSyncUpgrade,
    selectedCentersAndApps,
    isKickoffNotRequiredCC,
    isOnboardingRequired4Upgrade,
  ])

  // console.log({continueDisabled})
  // FULSAS-11317: removed dead code — creative call no longer triggered by website products
  useEffect(() => {
    if (selectedCentersAndApps) {
      setValue('CCOnboarding', true)
    } else {
      setValue('CCOnboarding', false)
    }
  }, [selectedCentersAndApps, setValue])

  useEffect(() => {
    if (hasCalledNextStep.current) return
    if (
      !displayKickOffCall &&
      !displayCreativeCallModal &&
      !displayThryvSuccessServicesCall &&
      (selectedCentersAndApps || selectedAddons)
    ) {
      hasCalledNextStep.current = true
      handleNextStep()
    }
  }, [
    displayCreativeCallModal,
    displayKickOffCall,
    displayThryvSuccessServicesCall,
    selectedCentersAndApps,
    selectedAddons,
    handleNextStep,
  ])
  console.log({
    isKickoffNotRequiredCC,
    continueDisabled,
    isOnboardingRequired4Upgrade,
    continueBtnStatus: getDisabledButton(),
  })

  return (
    <>
      <NewOrderLayout
        prevStep={prevStep}
        showContinueButton={true}
        showCancelButton={true}
        disableNextButton={getDisabledButton()}
        nextStep={handleSubmit(handleNextStep)}
        showModal={
          onboardingConfirmed ||
          creativeConfirmed ||
          thryvSuccessServicesConfirmed
        }
        kickoffApptId={kickoffApptId}
        creativeApptId={creativeApptId}
        thryvSuccessServicesApptId={thryvSuccessServicesApptId}
        countryISOValue={selectedCountry.isoValue}
        setCreativeConfirmed={setCreativeConfirmed}
        setOnboardingConfirmed={setOnboardingConfirmed}
        setOnboardingCallDate={setOnboardingCallDate}
        setCreativeCallDate={setCreativeCallDate}
        order5NUId={order5NUId}
        isCancelOrder5={isCancelOrder5}
        cancelOrder5={cancelOrder5}
        captureOrderNewUpgrade={captureOrderNewUpgrade}
        order5OnboardingApptId={order5OnboardingApptId}
        order5ThryvSuccessServicesApptId={order5ThryvSuccessServicesApptId}
        order5CreativeApptId={order5CreativeApptId}
        setOrder5CreativeApptId={setOrder5CreativeApptId}
        setOrder5OnboardingApptId={setOrder5OnboardingApptId}
        setOrder5ThryvSuccessServicesApptId={
          setOrder5ThryvSuccessServicesApptId
        }
        isBillingNoticeDisplayed={isBillingNoticeDisplayed}
        setIsBillingNoticeDisplayed={setIsBillingNoticeDisplayed}
      >
        {selectedCentersAndApps && (
          <div>
            <AccountInfoBanner
              thryvId={
                upgradeAccountInfo?.thryvID || accountInformation?.thryvId
              }
              companyName={
                upgradeAccountInfo?.recurlyCompany ||
                accountInformation?.company
              }
              firstName={
                upgradeAccountInfo?.recurlyFirst ||
                accountInformation?.firstName
              }
              lastName={
                upgradeAccountInfo?.recurlyLast || accountInformation?.lastName
              }
              billTo={upgradeAccountInfo?.billTo || accountInformation?.billTo}
              style={{marginLeft: '20px', marginTop: '20px'}}
            />
            <CcSidebar
              selectedCentersAndApps={selectedCentersAndApps}
              all5PlanPrices={all5PlanPrices}
              country={selectedCountry.isoValue}
              eligibleTransitionCenters={eligibleTransitionCenters}
              defaultCentersAndApps={defaultCentersAndApps}
              previewRequestFinished={previewRequestFinished}
              isCohort={isCohort}
              accountInfo={{
                ...accountInformation,
                balanceInfo: upgradeAccountInfo?.balanceInfo || {},
              }}
              isWebsiteValid={isWebsiteValid}
              isNewTLMCAccessible={isNewTLMCAccessible}
            />
          </div>
        )}
        {!selectedCentersAndApps && (
          <div>
            <AccountInfoBanner
              thryvId={
                upgradeAccountInfo?.thryvID || accountInformation?.thryvId
              }
              companyName={
                upgradeAccountInfo?.recurlyCompany ||
                accountInformation?.company
              }
              firstName={
                upgradeAccountInfo?.recurlyFirst ||
                accountInformation?.firstName
              }
              lastName={
                upgradeAccountInfo?.recurlyLast || accountInformation?.lastName
              }
              billTo={upgradeAccountInfo?.billTo || accountInformation?.billTo}
              style={{marginLeft: '20px', marginTop: '20px'}}
            />
            <CartPaymentDetails
              listOfAddons={selectedAddons}
              selectedCountry={selectedCountry.value}
              currentStep={currentStep}
              setCartProducts={setCartProducts}
              cartProducts={cartProducts}
              totalDiscount={0}
              currentPlanName={currentPlanName}
              upgradeAccountInfo={upgradeAccountInfo}
            />
          </div>
        )}
        <OnboardingContainer
          style={
            selectedCentersAndApps
              ? {
                  borderLeft: '1px solid #CCCCCC',
                  gridColumnStart: 2,
                  alignContent: 'start',
                }
              : {}
          }
        >
          <div
            className={
              !selectedCentersAndApps ? 'pl-4 pr-4 pt-4' : 'pl-4 pb-4 pr-4'
            }
          >
            <ParagraphText variant="lg" className="font-weight-bold">
              {selectedCentersAndApps
                ? 'Onboarding Contact'
                : 'ONBOARDING CONTACT'}
            </ParagraphText>
            <InputContainer
              className={!selectedCentersAndApps ? 'mb-5' : 'mb-2'}
            >
              <div className="mb-2">
                <Input
                  className="w-100"
                  type="text"
                  placeholder="First Name"
                  aria-label="First Name"
                  name="firstName"
                  withLabel
                  labelType="floating"
                  variant="default"
                  disabled={useAccountOwnerInfo}
                  register={register}
                  errors={errors}
                />
              </div>
              <Input
                className="w-100"
                type="text"
                placeholder="Last Name"
                aria-label="Last Name"
                name="lastName"
                withLabel
                labelType="floating"
                variant="default"
                disabled={useAccountOwnerInfo}
                register={register}
                errors={errors}
              />
              <div className="mb-2">
                <Input
                  className="w-100"
                  type="text"
                  placeholder="Email Address"
                  aria-label="Email Address"
                  name="email"
                  withLabel
                  labelType="floating"
                  variant="default"
                  disabled={useAccountOwnerInfo}
                  register={register}
                  errors={errors}
                />
              </div>
              <Input
                className="w-100"
                type="text"
                placeholder="Phone Number"
                aria-label="Phone Number"
                name="mobile"
                withLabel
                labelType="floating"
                variant="default"
                disabled={useAccountOwnerInfo}
                register={register}
                errors={errors}
              />
              <Checkbox
                hasLabel
                name="HTML"
                isChecked={useAccountOwnerInfo}
                onChange={handleOnChange}
                label="Same as account owner"
              />
            </InputContainer>
            {isPurchaseIntentVisible && !selectedCentersAndApps && (
              <>
                <ParagraphText variant="lg" className="font-weight-bold">
                  PURCHASE INTENT
                </ParagraphText>

                <ParagraphText variant="reg" color="thryv-gray-medium-500">
                  What features is client most interested in?
                </ParagraphText>
                <PurchaseIntentGroupContainer>
                  <PurchaseIntentModal
                    parentProps={{
                      title: 'First Purchase Intent',
                      purchaseIntent: firstPurchaseIntent,
                      errors: {errors},
                      name: 'firstPurchaseIntent',
                    }}
                    modalProps={{
                      setSelectedPurchaseIntent: setFirstPurchaseIntent,
                      selectedPurchaseIntents: [
                        secondPurchaseIntent.option,
                        thirdPurchaseIntent.option,
                      ],
                      selectedPurchaseIntent: firstPurchaseIntent,
                    }}
                  />
                  <PurchaseIntentModal
                    parentProps={{
                      title: 'Second Purchase Intent',
                      purchaseIntent: secondPurchaseIntent,
                      errors: {errors},
                      name: 'secondPurchaseIntent',
                    }}
                    modalProps={{
                      setSelectedPurchaseIntent: setSecondPurchaseIntent,
                      selectedPurchaseIntents: [
                        firstPurchaseIntent.option,
                        thirdPurchaseIntent.option,
                      ],
                      selectedPurchaseIntent: secondPurchaseIntent,
                    }}
                  />
                  <PurchaseIntentModal
                    parentProps={{
                      title: 'Third Purchase Intent',
                      purchaseIntent: thirdPurchaseIntent,
                      errors: {errors},
                      name: 'thirdPurchaseIntent',
                    }}
                    modalProps={{
                      setSelectedPurchaseIntent: setThirdPurchaseIntent,
                      selectedPurchaseIntents: [
                        firstPurchaseIntent.option,
                        secondPurchaseIntent.option,
                      ],
                      selectedPurchaseIntent: thirdPurchaseIntent,
                    }}
                  />
                </PurchaseIntentGroupContainer>
              </>
            )}
          </div>
          {isPurchaseIntentVisible && !isKickoffNotRequiredCC && (
            <div
              className={
                !selectedCentersAndApps ? 'pb-4 pl-4 pr-4' : 'pt-4 pr-4 pl-4'
              }
            >
              <ParagraphText
                variant={!selectedCentersAndApps ? 'reg' : 'lg'}
                color={!selectedCentersAndApps && 'thryv-gray-medium-500'}
              >
                Is there anything else you want the CSM to know about the
                business and why they are purchasing Thryv?
              </ParagraphText>
              <TextAreaContainer>
                <TextArea
                  type="text"
                  name="csmExtraInfo"
                  limit={750}
                  register={register}
                  errors={errors}
                  height={'10px'}
                />
              </TextAreaContainer>
            </div>
          )}
          {(continueDisabled ||
            getDisabledButton() ||
            (!isKickoffNotRequiredCC && isKickoffNotRequiredCC !== undefined) ||
            (isOnboardingRequired4Upgrade &&
              isOnboardingRequired4Upgrade !== undefined)) && (
            <BookAppointments
              selectedCentersAndApps={selectedCentersAndApps}
              saveCallData={saveCallData}
              setDisplayCreativeCallModal={setDisplayCreativeCallModal}
              setDisplayKickOffCall={setDisplayKickOffCall}
              setDisplayThryvSuccessServicesCall={
                setDisplayThryvSuccessServicesCall
              }
              setOnboardingConfirmed={setOnboardingConfirmed}
              setCreativeConfirmed={setCreativeConfirmed}
              setThryvSuccessServicesConfirmed={
                setThryvSuccessServicesConfirmed
              }
              setIsApptSkipped={setIsApptSkipped}
              selectedCountry={selectedCountry}
              selectedAddons={selectedAddons}
              selectedIndustryId={selectedIndustryId}
              onboardingCallDate={onboardingCallDate}
              useAccountOwnerInfo={useAccountOwnerInfo}
              accountInformation={accountInformation}
              upgradeAccountInfo={upgradeAccountInfo}
              isApptSkipped={isApptSkipped}
              setKickoffApptId={setKickoffApptId}
              kickoffApptId={kickoffApptId}
              setCreativeApptId={setCreativeApptId}
              creativeApptId={creativeApptId}
              setThryvSuccessServicesApptId={setThryvSuccessServicesApptId}
              thryvSuccessServicesApptId={thryvSuccessServicesApptId}
              setOnboardingCallDate={setOnboardingCallDate}
              creativeCallDate={creativeCallDate}
              setCreativeCallDate={setCreativeCallDate}
              thryvSuccessServicesCallDate={thryvSuccessServicesCallDate}
              setThryvSuccessServicesCallDate={setThryvSuccessServicesCallDate}
              onboardingConfirmed={onboardingConfirmed}
              creativeConfirmed={creativeConfirmed}
              thryvSuccessServicesConfirmed={thryvSuccessServicesConfirmed}
              displayKickOffCall={displayKickOffCall}
              displayThryvSuccessServicesCall={displayThryvSuccessServicesCall}
              displayCreativeCallModal={displayCreativeCallModal}
              isSyncUpgrade={isSyncUpgrade}
              industryCategoryType={industryCategoryType}
              isKickoffNotRequiredCC={isKickoffNotRequiredCC}
              isThryvStarterOnboarding={isThryvStarterOnboarding}
              defaultCentersAndApps={defaultCentersAndApps}
              order5NUId={order5NUId}
              captureOrderNewUpgrade={captureOrderNewUpgrade}
              setOrder5CreativeApptId={setOrder5CreativeApptId}
              setOrder5OnboardingApptId={setOrder5OnboardingApptId}
              setOrder5ThryvSuccessServicesApptId={
                setOrder5ThryvSuccessServicesApptId
              }
              input={{
                firstNameInput,
                lastNameInput,
                emailInput,
                mobileInput,
              }}
              purchaseIntent={{
                firstPurchaseIntent,
                secondPurchaseIntent,
                thirdPurchaseIntent,
              }}
              useFormData={{
                register,
                errors,
                trigger,
                clearErrors,
                setValue,
              }}
              isNewTLMCAccessible={isNewTLMCAccessible}
              keapAccount={keapAccount}
            />
          )}
        </OnboardingContainer>
      </NewOrderLayout>
    </>
  )
}

export {OnboardingProgram}
